import type { AssignmentTemplatePayload } from '../interfaces'
import type { AssessmentValidationIssue } from '../../assessments/interfaces'
import type {
  AssignmentChapter,
  AssignmentContentBlock,
  AssignmentContentOwner,
  AssignmentEditorStore,
  MathExercise,
} from './interfaces'

import { isRecord as isPlainObject } from '@shared/lib/collections'

import { AssignmentType } from '../enums'

/** Key-order-insensitive deep equality: the `json` column reorders keys on write. */
const isSameValue = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false
    }
    return left.every((item, index) => isSameValue(item, right[index]))
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left)
    if (leftKeys.length !== Object.keys(right).length) {
      return false
    }
    return leftKeys.every(key =>
      Object.prototype.hasOwnProperty.call(right, key) && isSameValue(left[key], right[key]),
    )
  }
  return false
}

/** Stored `data` is unvalidated, so guard the shape. Returned by reference (mutations splice it);
 *  bad elements are guarded at each read site. */
const getChapters = (state: AssignmentEditorStore): AssignmentChapter[] => {
  const definition: unknown = state.workingDocument?.definition
  const screens: unknown = isPlainObject(definition) ? definition.screens : null

  return Array.isArray(screens) ? screens as AssignmentChapter[] : []
}

const getSelectedChapter = (state: AssignmentEditorStore): AssignmentChapter | null =>
  getChapters(state).find(chapter => chapter?.id === state.selectedChapterId) ?? null

/** Same guard as `getChapters`, over a MATH definition's `exercises`. */
const getExercises = (state: AssignmentEditorStore): MathExercise[] => {
  const definition: unknown = state.workingDocument?.definition
  const exercises: unknown = isPlainObject(definition) ? definition.exercises : null

  return Array.isArray(exercises) ? exercises as MathExercise[] : []
}

const getSelectedExercise = (state: AssignmentEditorStore): MathExercise | null =>
  getExercises(state).find(exercise => exercise?.id === state.selectedExerciseId) ?? null

/** A chapter's `content` or an exercise's `prompt`, `[]` when the owner or its list is gone. */
const getContentOf = (state: AssignmentEditorStore, owner: AssignmentContentOwner): AssignmentContentBlock[] => {
  const list: unknown = 'exerciseId' in owner
    ? getExercises(state).find(exercise => exercise?.id === owner.exerciseId)?.prompt
    : getChapters(state).find(chapter => chapter?.id === owner.screenId)?.content

  return Array.isArray(list) ? list as AssignmentContentBlock[] : []
}

const definitionString = (state: AssignmentEditorStore, key: 'title' | 'description'): string => {
  const definition: unknown = state.workingDocument?.definition
  const value: unknown = isPlainObject(definition) ? definition[key] : null

  return typeof value === 'string' ? value : ''
}

/** The full `store`/`update` body, or `null` while a required field is missing. Always complete —
 *  `POST` requires every field; a complete body is also a legal `PATCH`. Shared with `buildSavePayload`. */
export const toSavePayload = (state: AssignmentEditorStore): AssignmentTemplatePayload | null => {
  if (!state.workingDocument || !state.type || !state.status || !state.grade) {
    return null
  }
  return {
    title: state.title,
    type: state.type,
    status: state.status,
    grade: state.grade,
    cover_path: state.coverPath,
    curriculum_topic_id: state.curriculumTopicId,
    // Always sent beside the topic: the API vets the pair.
    curriculum_lesson_id: state.curriculumLessonId,
    data: state.workingDocument,
  }
}

export const getters = {
  isLoading: (state: AssignmentEditorStore): AssignmentEditorStore['loading'] => state.loading,
  isSaving: (state: AssignmentEditorStore): AssignmentEditorStore['saving'] => state.saving,
  hasDocument: (state: AssignmentEditorStore): boolean => state.workingDocument !== null,
  getTemplateId: (state: AssignmentEditorStore): AssignmentEditorStore['templateId'] => state.templateId,

  // ---- row fields ----
  getTitle: (state: AssignmentEditorStore): AssignmentEditorStore['title'] => state.title,
  getType: (state: AssignmentEditorStore): AssignmentEditorStore['type'] => state.type,
  getStatus: (state: AssignmentEditorStore): AssignmentEditorStore['status'] => state.status,
  getDisciplineCode: (state: AssignmentEditorStore): AssignmentEditorStore['disciplineCode'] => state.disciplineCode,
  getGrade: (state: AssignmentEditorStore): AssignmentEditorStore['grade'] => state.grade,
  getCoverPath: (state: AssignmentEditorStore): AssignmentEditorStore['coverPath'] => state.coverPath,
  /** Signed, render-only — never persist. */
  getCoverUrl: (state: AssignmentEditorStore): AssignmentEditorStore['coverUrl'] => state.coverUrl,
  hasCover: (state: AssignmentEditorStore): boolean => state.coverPath !== null,

  // ---- taxonomy ----
  getCurriculumTopicId: (state: AssignmentEditorStore): AssignmentEditorStore['curriculumTopicId'] => state.curriculumTopicId,
  getCurriculumLessonId: (state: AssignmentEditorStore): AssignmentEditorStore['curriculumLessonId'] => state.curriculumLessonId,
  getTaxonomyLabels: (state: AssignmentEditorStore): AssignmentEditorStore['taxonomyLabels'] => state.taxonomyLabels,
  isStale: (state: AssignmentEditorStore): boolean => state.taxonomyLabels?.is_stale === true,

  // ---- definition-level fields ----
  /** Kid-facing book name; `getTitle` is the catalogue handle. */
  getDefinitionTitle: (state: AssignmentEditorStore): string =>
    definitionString(state, 'title'),
  getDefinitionDescription: (state: AssignmentEditorStore): string =>
    definitionString(state, 'description'),

  /** Raw, never a parsed copy: unknown keys must survive the round trip. */
  getDocument: (state: AssignmentEditorStore): AssignmentEditorStore['workingDocument'] =>
    state.workingDocument,

  /** Read-only view; saves must go through the `buildSavePayload` action (reconciles the media manifest). */
  getPayload: (state: AssignmentEditorStore): AssignmentTemplatePayload | null =>
    toSavePayload(state),

  isDirty: (state: AssignmentEditorStore): boolean => {
    if (!state.workingDocument || !state.pristine) {
      return false
    }
    return state.title !== state.pristine.title
      || state.type !== state.pristine.type
      || state.status !== state.pristine.status
      || state.grade !== state.pristine.grade
      || state.coverPath !== state.pristine.coverPath
      || state.curriculumTopicId !== state.pristine.curriculumTopicId
      || state.curriculumLessonId !== state.pristine.curriculumLessonId
      || !isSameValue(state.workingDocument, state.pristine.document)
  },

  // ---- chapters & selection ----
  getChapters: (state: AssignmentEditorStore): AssignmentChapter[] => getChapters(state),
  hasChapters: (state: AssignmentEditorStore): boolean => getChapters(state).length > 0,
  getChapterCount: (state: AssignmentEditorStore): number => getChapters(state).length,
  getSelectedChapterId: (state: AssignmentEditorStore): AssignmentEditorStore['selectedChapterId'] =>
    state.selectedChapterId,
  getSelectedChapter: (state: AssignmentEditorStore): AssignmentChapter | null => getSelectedChapter(state),
  getSelectedChapterIndex: (state: AssignmentEditorStore): number =>
    getChapters(state).findIndex(chapter => chapter?.id === state.selectedChapterId),

  /** The block list behind a content owner — what the shared block list renders. */
  getContentOf: (state: AssignmentEditorStore) => (owner: AssignmentContentOwner): AssignmentContentBlock[] =>
    getContentOf(state, owner),

  // ---- exercises & selection (MATH) ----
  isMath: (state: AssignmentEditorStore): boolean => state.type === AssignmentType.Math,
  getExercises: (state: AssignmentEditorStore): MathExercise[] => getExercises(state),
  hasExercises: (state: AssignmentEditorStore): boolean => getExercises(state).length > 0,
  getExerciseCount: (state: AssignmentEditorStore): number => getExercises(state).length,
  getSelectedExerciseId: (state: AssignmentEditorStore): AssignmentEditorStore['selectedExerciseId'] =>
    state.selectedExerciseId,
  getPromptTab: (state: AssignmentEditorStore): AssignmentEditorStore['promptTab'] => state.promptTab,
  getSelectedExercise: (state: AssignmentEditorStore): MathExercise | null => getSelectedExercise(state),
  getSelectedExerciseIndex: (state: AssignmentEditorStore): number =>
    getExercises(state).findIndex(exercise => exercise?.id === state.selectedExerciseId),

  // ---- validation & the publish gate ----
  getValidationIssues: (state: AssignmentEditorStore): AssessmentValidationIssue[] =>
    state.validationIssues ?? [],
  hasValidationReport: (state: AssignmentEditorStore): boolean => state.validationIssues !== null,
  hasValidationErrors: (state: AssignmentEditorStore): boolean => (state.validationIssues?.length ?? 0) > 0,

  /** The only publish gate — the API publishes anything. `null` (not validated) is not publishable. */
  isPublishable: (state: AssignmentEditorStore): boolean =>
    state.workingDocument !== null
    && state.validationIssues !== null
    && state.validationIssues.length === 0,

  // ---- signed media ----
  /** `null` when unsigned — render a placeholder, never the raw path (private bucket). */
  getMediaUrl: (state: AssignmentEditorStore) => (path: string): string | null =>
    state.mediaUrls[path] ?? null,
  getMediaUrls: (state: AssignmentEditorStore): AssignmentEditorStore['mediaUrls'] => state.mediaUrls,
  getMediaExpiresAt: (state: AssignmentEditorStore): AssignmentEditorStore['mediaExpiresAt'] => state.mediaExpiresAt,
}
